import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';

// Sample types that require manufactured vials
const VIAL_REQUIRED_TYPES = ['STERILITY', 'ENDOTOXIN'];
// All sample types
const ALL_SAMPLE_TYPES = ['STERILITY', 'ENDOTOXIN', 'BIOBURDEN', 'SEROLOGY'];

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const formatDate = (date) => {
  if (!date) return '-';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

const QCTestingTab = ({ donor, onRefresh }) => {
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [vialLotStart, setVialLotStart] = useState(donor?.vialLotStart || '0001');
  
  // QC Documents state
  const [qcDocuments, setQcDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileInputRef = useRef(null);
  
  const [newSample, setNewSample] = useState({
    sampleType: 'STERILITY',
    selectedVialId: '',
    quantity: '',
    labCost: '',
    costNotes: '',
    notes: ''
  });

  // Calculate totals
  const totalManufactured = (donor?.manufacturedVials || [])
    .reduce((sum, v) => sum + (v.quantity || 0), 0);
  
  // Get manufactured vials that are available for QC testing (not yet released)
  const manufacturedVials = (donor?.manufacturedVials || [])
    .filter(v => v.status !== 'RELEASED');
  
  // Get selected vial details
  const selectedVial = manufacturedVials.find(v => v.id === newSample.selectedVialId);
  
  // Calculate available quantity for selected vial (total - already used for QC)
  const getAvailableQuantity = (vialId) => {
    if (!vialId) return 0;
    const vial = manufacturedVials.find(v => v.id === vialId);
    if (!vial) return 0;
    
    // Count how many QC samples already used from this vial
    const qcSamplesFromVial = (donor?.qcSamples || [])
      .filter(s => s.manufacturedVialId === vialId)
      .reduce((sum, s) => sum + (s.quantity || 0), 0);
    
    return Math.max(0, (vial.quantity || 0) - qcSamplesFromVial);
  };
  
  const qcSamples = donor?.qcSamples || [];
  const totalSterility = qcSamples
    .filter(s => s.sampleType === 'STERILITY')
    .reduce((sum, s) => sum + (s.quantity || 0), 0);
  const totalEndotoxin = qcSamples
    .filter(s => s.sampleType === 'ENDOTOXIN')
    .reduce((sum, s) => sum + (s.quantity || 0), 0);
  const totalBioburden = qcSamples
    .filter(s => s.sampleType === 'BIOBURDEN')
    .reduce((sum, s) => sum + (s.quantity || 0), 0);
  const totalSerology = qcSamples
    .filter(s => s.sampleType === 'SEROLOGY')
    .reduce((sum, s) => sum + (s.quantity || 0), 0);
  const totalVialsForSale = qcSamples
    .filter(s => s.sampleType === 'VIALS_FOR_SALE')
    .reduce((sum, s) => sum + (s.quantity || 0), 0);
  
  // Check if each sample type has at least one entry
  const hasSterility = qcSamples.some(s => s.sampleType === 'STERILITY');
  const hasEndotoxin = qcSamples.some(s => s.sampleType === 'ENDOTOXIN');
  const hasBioburden = qcSamples.some(s => s.sampleType === 'BIOBURDEN');
  const hasSerology = qcSamples.some(s => s.sampleType === 'SEROLOGY');
  const allSampleTypesEntered = hasSterility && hasEndotoxin && hasBioburden && hasSerology;
  
  // Only STERILITY and ENDOTOXIN consume vials
  const totalQCSamples = totalSterility + totalEndotoxin;
  const totalSamples = totalSterility + totalEndotoxin + totalVialsForSale;
  const vialsRemaining = totalManufactured - totalSamples;
  
  // Calculate vials available for sale (manufactured - QC samples that consume vials)
  const vialsAvailableForSale = totalManufactured - totalQCSamples;

  useEffect(() => {
    setSamples(donor?.qcSamples || []);
    setQcDocuments(donor?.qcDocuments || []);
    if (donor?.vialLotStart) {
      setVialLotStart(donor.vialLotStart);
    }
    // Auto-select first available vial if none selected and vials exist
    if (!newSample.selectedVialId && manufacturedVials.length > 0 && 
        VIAL_REQUIRED_TYPES.includes(newSample.sampleType)) {
      setNewSample(prev => ({ ...prev, selectedVialId: manufacturedVials[0].id }));
    }
  }, [donor?.qcSamples, donor?.qcDocuments, donor?.vialLotStart, donor?.manufacturedVials]);

  const handleInputChange = (field, value) => {
    if (field === 'selectedVialId') {
      // When vial is selected, reset quantity
      setNewSample(prev => ({ ...prev, selectedVialId: value, quantity: '' }));
    } else {
      setNewSample(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleAddSample = async () => {
    const requiresVial = VIAL_REQUIRED_TYPES.includes(newSample.sampleType);
    const quantity = parseInt(newSample.quantity) || 0;

    // For STERILITY/ENDOTOXIN: require vial and quantity >= 1
    if (requiresVial) {
      if (!newSample.selectedVialId || !selectedVial) {
        setError('Please select a manufactured vial');
        return;
      }

      if (quantity <= 0) {
        setError('Please enter a valid quantity (at least 1)');
        return;
      }

      // Validate quantity against available vials
      const availableQty = getAvailableQuantity(newSample.selectedVialId);
      if (quantity > availableQty) {
        setError(`Cannot add ${quantity} samples. Only ${availableQty} vials available from selected batch.`);
        return;
      }
    }

    // For BIOBURDEN/SEROLOGY: quantity can be 0
    if (!requiresVial && (newSample.quantity === '' || newSample.quantity === undefined)) {
      setError('Please enter a quantity (can be 0)');
      return;
    }

    // Validate lab cost
    if (newSample.labCost && (isNaN(parseFloat(newSample.labCost)) || parseFloat(newSample.labCost) < 0)) {
      setError('Please enter a valid lab cost');
      return;
    }

    setAdding(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('authToken');
      
      // Prepare request body
      const requestBody = {
        sampleType: newSample.sampleType,
        quantity: quantity,
        labCost: newSample.labCost ? parseFloat(newSample.labCost) : undefined,
        costNotes: newSample.costNotes || undefined,
        notes: newSample.notes || undefined
      };

      // Add vial details only for STERILITY/ENDOTOXIN
      if (requiresVial && selectedVial) {
        requestBody.manufacturedVialId = selectedVial.id;
        requestBody.vialSize = selectedVial.vialSize || '1.5mL';
        requestBody.productCode = selectedVial.productSku || newSample.sampleType;
        requestBody.expirationDate = selectedVial.expirationDate 
          ? new Date(selectedVial.expirationDate).toISOString().split('T')[0]
          : undefined;
      }

      console.log('Sending QC sample request:', requestBody);

      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/qc-samples/enhanced`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.error || data.message || 'Failed to add QC sample';
        console.error('Backend validation error:', data);
        throw new Error(errorMessage);
      }

      const displayQty = quantity === 0 ? 'entry' : `${quantity} samples`;
      setSuccess(`${newSample.sampleType} ${displayQty} added`);
      setNewSample({
        sampleType: 'STERILITY',
        selectedVialId: manufacturedVials.length > 0 ? manufacturedVials[0].id : '',
        quantity: '',
        labCost: '',
        costNotes: '',
        notes: ''
      });
      
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Error adding QC sample:', err);
      setError(err.message || 'Failed to add QC sample');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteSample = async (sampleId) => {
    if (!window.confirm('Are you sure you want to remove this QC sample? Vials will be restored.')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/qc-samples/${sampleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete QC sample');
      }

      setSuccess('QC sample removed, vials restored');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Error deleting QC sample:', err);
      setError(err.message || 'Failed to delete QC sample');
    }
  };

  const handleSaveLotStart = async () => {
    if (!vialLotStart || vialLotStart.trim() === '') {
      setError('Please enter a lot start number');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/vial-lot-start`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ vialLotStart: vialLotStart.trim() })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save lot start');
      }

      setSuccess('Lot start number saved');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Error saving lot start:', err);
      setError(err.message || 'Failed to save lot start');
    }
  };

  const handleCompleteQCTesting = async () => {
    // Validate that all 4 sample types have been entered
    if (!hasSterility || totalSterility === 0) {
      setError('At least one sterility sample (qty >= 1) must be added before finalizing');
      return;
    }

    if (!hasEndotoxin || totalEndotoxin === 0) {
      setError('At least one endotoxin sample (qty >= 1) must be added before finalizing');
      return;
    }

    if (!hasBioburden) {
      setError('Bioburden test entry is required before finalizing (quantity can be 0)');
      return;
    }

    if (!hasSerology) {
      setError('Serology test entry is required before finalizing (quantity can be 0)');
      return;
    }

    if (!vialLotStart || vialLotStart.trim() === '') {
      setError('Please enter and save a lot start number for normal vials');
      return;
    }

    setCompleting(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/complete-qc-testing-enhanced`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ vialLotStart: vialLotStart.trim() })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to finalize QC testing');
      }

      setSuccess('QC testing finalized successfully. Donor moved to Product Release.');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Error finalizing QC testing:', err);
      setError(err.message || 'Failed to finalize QC testing');
    } finally {
      setCompleting(false);
    }
  };

  // QC Document handlers
  const handleDocumentUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only PDF, DOC, DOCX, JPG, and PNG files are supported.');
      event.target.value = '';
      return;
    }

    // Check max documents
    if (qcDocuments.length >= 4) {
      setError('Maximum of 4 QC documents allowed. Delete an existing document to upload a new one.');
      event.target.value = '';
      return;
    }

    setUploadingDoc(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentName', file.name);

      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/qc-documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload document');
      }

      setSuccess(`${file.name} uploaded successfully`);
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Error uploading QC document:', err);
      setError(err.message || 'Failed to upload document');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/qc-documents/${docId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete document');
      }

      setSuccess('Document deleted successfully');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Error deleting QC document:', err);
      setError(err.message || 'Failed to delete document');
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Summary Cards - Row 1: Vial counts */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">Total Manufactured</Typography>
              <Typography variant="h4" fontWeight="bold" color="primary">{totalManufactured}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2}>
          <Card sx={{ bgcolor: 'success.light' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption">Available for Sale</Typography>
              <Typography variant="h4" fontWeight="bold" color="success.dark">{vialsAvailableForSale}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                ({totalManufactured} - {totalQCSamples})
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2}>
          <Card sx={{ bgcolor: vialsRemaining > 0 ? 'grey.200' : 'error.light' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption">Remaining</Typography>
              <Typography variant="h4" fontWeight="bold">{vialsRemaining}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Summary Cards - Row 2: QC Test Status */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        QC Tests Required (all 4 must be entered):
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: hasSterility && totalSterility > 0 ? 'success.light' : 'warning.light' }}>
            <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="caption">Sterility</Typography>
              <Typography variant="h5" fontWeight="bold">{totalSterility}</Typography>
              <Chip 
                size="small" 
                label={hasSterility && totalSterility > 0 ? '✓ Complete' : 'Required'} 
                color={hasSterility && totalSterility > 0 ? 'success' : 'warning'}
                sx={{ mt: 0.5 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: hasEndotoxin && totalEndotoxin > 0 ? 'success.light' : 'warning.light' }}>
            <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="caption">Endotoxin</Typography>
              <Typography variant="h5" fontWeight="bold">{totalEndotoxin}</Typography>
              <Chip 
                size="small" 
                label={hasEndotoxin && totalEndotoxin > 0 ? '✓ Complete' : 'Required'} 
                color={hasEndotoxin && totalEndotoxin > 0 ? 'success' : 'warning'}
                sx={{ mt: 0.5 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: hasBioburden ? 'success.light' : 'warning.light' }}>
            <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="caption">Bioburden</Typography>
              <Typography variant="h5" fontWeight="bold">{totalBioburden}</Typography>
              <Chip 
                size="small" 
                label={hasBioburden ? '✓ Complete' : 'Required (qty can be 0)'} 
                color={hasBioburden ? 'success' : 'warning'}
                sx={{ mt: 0.5 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: hasSerology ? 'success.light' : 'warning.light' }}>
            <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="caption">Serology</Typography>
              <Typography variant="h5" fontWeight="bold">{totalSerology}</Typography>
              <Chip 
                size="small" 
                label={hasSerology ? '✓ Complete' : 'Required (qty can be 0)'} 
                color={hasSerology ? 'success' : 'warning'}
                sx={{ mt: 0.5 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* QC Testing Status */}
      {donor?.qcTestingComplete && (
        <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircleIcon />}>
          <Typography variant="body2">
            <strong>QC Testing Complete</strong> - Completed by {donor.qcTestingCompletedByName} on {formatDate(donor.qcTestingCompletedAt)}
          </Typography>
        </Alert>
      )}

      {/* Add Sample Form */}
      {!donor?.qcTestingComplete && totalManufactured > 0 && (
        <Card sx={{ mb: 3, p: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Add QC Sample
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={newSample.sampleType}
                  onChange={(e) => {
                    handleInputChange('sampleType', e.target.value);
                    // Auto-select first vial when switching to sterility/endotoxin
                    if (VIAL_REQUIRED_TYPES.includes(e.target.value) && manufacturedVials.length > 0) {
                      handleInputChange('selectedVialId', manufacturedVials[0].id);
                    } else {
                      // Clear vial selection for BIOBURDEN/SEROLOGY
                      handleInputChange('selectedVialId', '');
                    }
                  }}
                  label="Type"
                >
                  <MenuItem value="STERILITY">Sterility (requires vials)</MenuItem>
                  <MenuItem value="ENDOTOXIN">Endotoxin (requires vials)</MenuItem>
                  <MenuItem value="BIOBURDEN">Bioburden (qty can be 0)</MenuItem>
                  <MenuItem value="SEROLOGY">Serology (qty can be 0)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Manufactured Vial Selection - Only for STERILITY/ENDOTOXIN */}
            {VIAL_REQUIRED_TYPES.includes(newSample.sampleType) && (
              <>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Select Manufactured Vial *</InputLabel>
                    <Select
                      value={newSample.selectedVialId}
                      onChange={(e) => handleInputChange('selectedVialId', e.target.value)}
                      label="Select Manufactured Vial *"
                    >
                      {manufacturedVials.map((vial) => {
                        const available = getAvailableQuantity(vial.id);
                        const expDate = vial.expirationDate 
                          ? new Date(vial.expirationDate).toLocaleDateString()
                          : 'N/A';
                        return (
                          <MenuItem key={vial.id} value={vial.id} disabled={available <= 0}>
                            {vial.productName} - Qty: {vial.quantity} ({available} available) - Exp: {expDate}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Display selected vial details */}
                {selectedVial && (
                  <>
                    <Grid item xs={6} sm={1.5}>
                      <TextField
                        size="small"
                        label="Vial Size"
                        fullWidth
                        value={selectedVial.vialSize || '1.5mL'}
                        disabled
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        size="small"
                        label="Expiration Date"
                        type="date"
                        fullWidth
                        value={selectedVial.expirationDate 
                          ? new Date(selectedVial.expirationDate).toISOString().split('T')[0]
                          : ''}
                        disabled
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={1.5}>
                      <TextField
                        size="small"
                        label="Available"
                        fullWidth
                        value={getAvailableQuantity(selectedVial.id)}
                        disabled
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  </>
                )}
              </>
            )}

            {/* Info text for BIOBURDEN/SEROLOGY */}
            {!VIAL_REQUIRED_TYPES.includes(newSample.sampleType) && (
              <Grid item xs={12} sm={4}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  {newSample.sampleType} does not require vials. Quantity can be 0.
                </Alert>
              </Grid>
            )}

            <Grid item xs={6} sm={1.5}>
              <TextField
                size="small"
                label={VIAL_REQUIRED_TYPES.includes(newSample.sampleType) ? "Qty *" : "Qty"}
                type="number"
                fullWidth
                value={newSample.quantity}
                onChange={(e) => handleInputChange('quantity', e.target.value)}
                inputProps={{ 
                  min: VIAL_REQUIRED_TYPES.includes(newSample.sampleType) ? 1 : 0, 
                  max: VIAL_REQUIRED_TYPES.includes(newSample.sampleType)
                    ? getAvailableQuantity(newSample.selectedVialId)
                    : undefined
                }}
                required={VIAL_REQUIRED_TYPES.includes(newSample.sampleType)}
                placeholder={VIAL_REQUIRED_TYPES.includes(newSample.sampleType) ? "" : "0"}
              />
            </Grid>
            
            {/* Lab cost */}
            <Grid item xs={6} sm={1.5}>
              <TextField
                size="small"
                label="Lab Cost"
                type="number"
                fullWidth
                value={newSample.labCost}
                onChange={(e) => handleInputChange('labCost', e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                placeholder="0.00"
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <TextField
                size="small"
                label="Cost Notes"
                fullWidth
                value={newSample.costNotes}
                onChange={(e) => handleInputChange('costNotes', e.target.value)}
                placeholder="Optional notes about lab costs"
                multiline
                rows={1}
              />
            </Grid>
            
            <Grid item xs={6} sm={2}>
              <Button
                variant="contained"
                fullWidth
                startIcon={adding ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                onClick={handleAddSample}
                disabled={
                  adding || 
                  (VIAL_REQUIRED_TYPES.includes(newSample.sampleType) && (
                    !newSample.quantity || 
                    !newSample.selectedVialId || 
                    getAvailableQuantity(newSample.selectedVialId) <= 0
                  )) ||
                  (!VIAL_REQUIRED_TYPES.includes(newSample.sampleType) && newSample.quantity === '')
                }
              >
                {adding ? 'Adding...' : 'Add'}
              </Button>
            </Grid>
          </Grid>
        </Card>
      )}

      {/* Samples Table */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        QC Samples
      </Typography>

      {samples.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No QC samples added yet. Add sterility and endotoxin samples above.
        </Alert>
      ) : (
        <TableContainer sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell align="center"><strong>Qty</strong></TableCell>
                <TableCell><strong>Size</strong></TableCell>
                <TableCell><strong>Product Code</strong></TableCell>
                <TableCell><strong>Expiration Date</strong></TableCell>
                <TableCell><strong>Lab Cost</strong></TableCell>
                <TableCell><strong>Sent At</strong></TableCell>
                <TableCell><strong>Sent By</strong></TableCell>
                {!donor?.qcTestingComplete && (
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {samples.map((sample) => (
                <TableRow key={sample.id} hover>
                  <TableCell>
                    <Chip 
                      label={
                        sample.sampleType === 'VIALS_FOR_SALE' ? 'Vials for Sale' : 
                        sample.sampleType === 'BIOBURDEN' ? 'Bioburden' :
                        sample.sampleType === 'SEROLOGY' ? 'Serology' :
                        sample.sampleType
                      } 
                      size="small"
                      color={
                        sample.sampleType === 'STERILITY' ? 'info' : 
                        sample.sampleType === 'ENDOTOXIN' ? 'warning' : 
                        sample.sampleType === 'BIOBURDEN' ? 'primary' :
                        sample.sampleType === 'SEROLOGY' ? 'success' :
                        'secondary'
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight="bold">{sample.quantity}</Typography>
                  </TableCell>
                  <TableCell>{sample.vialSize || '-'}</TableCell>
                  <TableCell>{sample.productCode || sample.sampleType}</TableCell>
                  <TableCell>{formatDate(sample.expirationDate)}</TableCell>
                  <TableCell>
                    {sample.labCost ? `$${parseFloat(sample.labCost).toFixed(2)}` : '-'}
                    {sample.costNotes && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {sample.costNotes}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(sample.sentAt)}</TableCell>
                  <TableCell>{sample.sentByName || '-'}</TableCell>
                  {!donor?.qcTestingComplete && (
                    <TableCell align="right">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDeleteSample(sample.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Lot Start for Normal Vials */}
      {!donor?.qcTestingComplete && totalManufactured > 0 && (
        <Card sx={{ mb: 3, p: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Lot Start for Normal Vials
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the starting lot number for the vials that will be created during product release. 
            This will be used to generate sequential lot numbers for {vialsAvailableForSale} vials (after subtracting {totalQCSamples} QC samples).
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Lot Start Number"
                value={vialLotStart}
                onChange={(e) => setVialLotStart(e.target.value)}
                placeholder="0001"
                helperText="Default: 0001 (can be changed as needed)"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleSaveLotStart}
                disabled={!vialLotStart || vialLotStart.trim() === ''}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </Card>
      )}

      {/* Finalize QC Testing Button */}
      {!donor?.qcTestingComplete && totalManufactured > 0 && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {(!hasSterility || totalSterility === 0) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                At least one sterility sample (qty ≥ 1) is required before finalizing.
              </Alert>
            )}
            {(!hasEndotoxin || totalEndotoxin === 0) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                At least one endotoxin sample (qty ≥ 1) is required before finalizing.
              </Alert>
            )}
            {!hasBioburden && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Bioburden test entry is required before finalizing (quantity can be 0).
              </Alert>
            )}
            {!hasSerology && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Serology test entry is required before finalizing (quantity can be 0).
              </Alert>
            )}
            {allSampleTypesEntered && totalSterility > 0 && totalEndotoxin > 0 && !vialLotStart && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Please enter and save a lot start number before finalizing.
              </Alert>
            )}
            {allSampleTypesEntered && totalSterility > 0 && totalEndotoxin > 0 && vialLotStart && (
              <Alert severity="info" sx={{ mb: 2 }}>
                All 4 QC tests entered. Ready to finalize. This will move the donor to Product Release.
              </Alert>
            )}
          </Typography>
          <Button
            variant="contained"
            color="success"
            size="large"
            startIcon={completing ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
            onClick={handleCompleteQCTesting}
            disabled={
              completing || 
              !allSampleTypesEntered || 
              totalSterility === 0 || 
              totalEndotoxin === 0 || 
              !vialLotStart || 
              vialLotStart.trim() === ''
            }
            sx={{ px: 4, py: 1.5 }}
          >
            {completing ? 'Finalizing...' : 'Finalize QC Testing'}
          </Button>
        </Box>
      )}

      {totalManufactured === 0 && (
        <Alert severity="warning">
          No vials have been manufactured yet. Complete manufacturing before adding QC samples.
        </Alert>
      )}

      {/* QC Documents Section */}
      <Divider sx={{ my: 3 }} />
      
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        QC Documents
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload up to 4 QC testing documents (lab reports, certificates, etc.)
      </Typography>

      {/* Document Upload */}
      {!donor?.qcTestingComplete && qcDocuments.length < 4 && (
        <Card sx={{ mb: 2, p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={handleDocumentUpload}
            />
            <Button
              variant="outlined"
              startIcon={uploadingDoc ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingDoc}
            >
              {uploadingDoc ? 'Uploading...' : 'Upload Document'}
            </Button>
            <Typography variant="body2" color="text.secondary">
              {qcDocuments.length}/4 documents uploaded
            </Typography>
          </Stack>
        </Card>
      )}

      {/* Documents List */}
      {qcDocuments.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No QC documents uploaded yet.
        </Alert>
      ) : (
        <TableContainer sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><strong>Document Name</strong></TableCell>
                <TableCell><strong>Uploaded</strong></TableCell>
                <TableCell><strong>Uploaded By</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {qcDocuments.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PdfIcon color="error" fontSize="small" />
                      <Typography variant="body2">{doc.fileName || doc.documentName}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{formatDate(doc.uploadedAt)}</TableCell>
                  <TableCell>{doc.uploadedByName || '-'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {doc.documentUrl && (
                        <Tooltip title="View Document">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => window.open(doc.documentUrl, '_blank', 'noopener,noreferrer')}
                          >
                            View
                          </Button>
                        </Tooltip>
                      )}
                      {!donor?.qcTestingComplete && (
                        <Tooltip title="Delete Document">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteDocument(doc.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default QCTestingTab;

